// API Helper with Bearer Token Authentication

// Backend base URL - empty means relative paths, which the dev server proxy forwards to the backend
// This avoids CORS issues in development. In production, set this to the backend's full URL.
const BACKEND_URL = process.env.REACT_APP_OFFER_LELO_BACKEND || '';

// API Endpoints from environment
export const API_ENDPOINTS = {
  OFFERS: process.env.REACT_APP_API_OFFERS || `${BACKEND_URL}/api/offers`,
  OFFERS_BY_STORE: process.env.REACT_APP_API_OFFERS_BY_STORE || `${BACKEND_URL}/api/offers/store`,
  FEATURED_OFFERS: process.env.REACT_APP_API_FEATURED_OFFERS || `${BACKEND_URL}/api/featured-offers`,
  BLOGS: process.env.REACT_APP_API_BLOGS || `${BACKEND_URL}/api/blogs`,
  SEARCH: process.env.REACT_APP_API_SEARCH || `${BACKEND_URL}/api/search`,
  S3_PRESIGNED_URL: process.env.REACT_APP_S3_PRESIGNED_URL_ENDPOINT || `${BACKEND_URL}/api/s3/presigned-url`,
  UPLOAD: process.env.REACT_APP_API_UPLOAD || `${BACKEND_URL}/api/upload`,
};

// Cognito config from environment
export const COGNITO_CONFIG = {
  DOMAIN: process.env.REACT_APP_COGNITO_DOMAIN || 'sravankoutam.auth.us-east-1.amazoncognito.com',
  CLIENT_ID: process.env.REACT_APP_COGNITO_CLIENT_ID || '2f5b879tsam3j6c080r2l1av62',
  REDIRECT_URI: process.env.REACT_APP_COGNITO_REDIRECT_URI || 'http://localhost:3006/callback',
};

/**
 * Get the stored access token from localStorage
 */
export const getAccessToken = () => {
  return localStorage.getItem('accessToken');
};

/**
 * Clear session and redirect to home for re-login (called on 401)
 */
export const clearSessionAndRedirect = () => {
  localStorage.removeItem('isAdmin');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('idToken');
  localStorage.removeItem('authCode');
  localStorage.removeItem('tokenTimestamp');
  localStorage.removeItem('expiresIn');
  window.location.href = '/';
};

/**
 * Get the stored ID token from localStorage
 */
export const getIdToken = () => {
  return localStorage.getItem('idToken');
};

/**
 * Check if the token is expired
 */
export const isTokenExpired = () => {
  const timestamp = localStorage.getItem('tokenTimestamp');
  const expiresIn = localStorage.getItem('expiresIn');
  
  if (!timestamp || !expiresIn) return true;
  
  const expirationTime = parseInt(timestamp) + (parseInt(expiresIn) * 1000);
  return Date.now() > expirationTime;
};

/**
 * Make an authenticated API call with Bearer token
 * @param {string} url - The API endpoint URL
 * @param {object} options - Fetch options (method, body, etc.)
 * @returns {Promise} - The fetch response
 */
export const apiCall = async (url, options = {}) => {
  const accessToken = getAccessToken();
  
  if (!accessToken) {
    throw new Error('No access token found. Please login again.');
  }
  
  if (isTokenExpired()) {
    throw new Error('Token expired. Please login again.');
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${accessToken}`,
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  return response;
};

/**
 * Example: GET request with Bearer token
 */
export const apiGet = async (url) => {
  return apiCall(url, { method: 'GET' });
};

/**
 * Example: POST request with Bearer token
 */
export const apiPost = async (url, data) => {
  return apiCall(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Example: PUT request with Bearer token
 */
export const apiPut = async (url, data) => {
  return apiCall(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Example: DELETE request with Bearer token
 */
export const apiDelete = async (url) => {
  return apiCall(url, { method: 'DELETE' });
};
